import { getTimelineEntryDate } from '../../../services/timeline/dateUtils';
import { getTimelinePlannerCardKey } from '../../../services/timeline/timelineDeduping';
import { getCaptureFirstTimelineContext } from './captureFirstTimelineContext';
import { getSourceFileName } from '../../../shared/sourceTitleResolver';

const normalizeText = (value = '') => String(value || '').trim();

const toDate = (value = null) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
};

const formatDateKey = (date = null) => date ? [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0'),
].join('-') : '';

export const getSourceNoteId = (entry = {}) => normalizeText(
  entry.sourceNoteId || entry.metadata?.sourceNoteId || entry.source?.sourceNoteId || ''
);

export const isMedicationReviewSummaryEntry = (entry = {}) => {
  const type = normalizeText(entry.type || entry.timelineType).toLowerCase();
  return type === 'medication_review_summary'
    || type === 'medication_review_annotation'
    || Boolean(entry.sourceReceiptAnnotation && entry.medicationReviewSummary);
};

export const getMedicationReviewSourceId = (entry = {}) => (
  isMedicationReviewSummaryEntry(entry)
    ? normalizeText(entry.sourceId || entry.sourceDocument?.sourceId || '')
    : ''
);

const getSourceContext = (entry = {}) => normalizeText(
  entry.sourceContext
  || entry.sourceMetadata?.sourceContext
  || entry.metadata?.source?.sourceContext
).toLowerCase();

const getMedicationReviewReceiptSummary = (summary = {}) => {
  const addedCount = Number(summary.addedCount || 0);
  const updatedCount = Number(summary.updatedCount || 0);
  if (!addedCount && !updatedCount) return 'No changes made';

  return [
    addedCount ? `${addedCount} added` : '',
    updatedCount ? `${updatedCount} updated` : '',
    Number(summary.unchangedCount || 0) ? `${summary.unchangedCount} unchanged` : '',
    Number(summary.notAddedCount || 0) ? `${summary.notAddedCount} not added` : '',
  ].filter(Boolean).join(' · ');
};

const getMedicationSuggestionCount = (entries = []) => Number(entries
  .map((entry) => (
    entry.medicationSuggestionSummary?.suggestionCount
    || entry.metadata?.medicationSuggestionSummary?.suggestionCount
    || 0
  ))
  .find((count) => Number(count) > 0) || 0);

const getPendingMedicationReceiptSummary = (entries = []) => {
  const suggestionCount = getMedicationSuggestionCount(entries);
  return suggestionCount
    ? `${suggestionCount} medication${suggestionCount === 1 ? '' : 's'} to review`
    : 'Waiting for review';
};

const getSourceContainerKind = (entry = {}) => normalizeText(
  entry.sourceContainerKind
  || entry.metadata?.timelinePlanner?.sourceContainerKind
  || entry.metadata?.timelinePlanner?.sourceContainer?.kind
).toLowerCase();

const getSourceType = (entry = {}) => normalizeText(
  entry.sourceMode
  || entry.inputMode
  || entry.sourceType
  || entry.source?.type
  || entry.source?.mode
  || entry.sourceMetadata?.inputMode
  || entry.sourceMetadata?.sourceType
  || entry.metadata?.sourceMode
  || entry.metadata?.inputMode
  || entry.metadata?.source?.inputMode
  || entry.metadata?.source?.sourceType
  || entry.metadata?.captureFirst?.sourceType
).toLowerCase();

export const isEmailSourceEntry = (entry = {}) => (
  /(?:^|_)(?:email|gmail|incoming_message)(?:_|$)/.test(getSourceType(entry))
);

export const isEmailMessageWrapperEntry = (entry = {}) => {
  if (!isEmailSourceEntry(entry)) return false;
  const title = normalizeText(entry.title || entry.displayTitle || entry.titlePrefix).toLowerCase();
  return title === 'email received' || title === 'forwarded email' || title === 'full email';
};

const CAPTURE_DAY_RECEIPT_SOURCE_PATTERN = /(?:^|_)(?:import|incoming|email|gmail|message|document|file|pdf|upload|photo|image|camera|screenshot|ocr)(?:_|$)/;
const MANUAL_NOTE_SOURCE_TYPES = new Set([
  'typed_long_note',
  'long_note_email',
  'quick_check_in',
  'quick_log',
  'quick_log_backend',
  'quick_log_capture_first',
  'dictated_text',
]);

export const isManualNoteSourceEntry = (entry = {}) => MANUAL_NOTE_SOURCE_TYPES.has(getSourceType(entry));

export const isCaptureDayReceiptEligibleEntry = (entry = {}) => {
  const sourceType = getSourceType(entry);
  const sourceKind = normalizeText(
    entry.sourceKind
    || entry.source?.sourceKind
    || entry.metadata?.source?.sourceKind
  ).toLowerCase();

  if (isManualNoteSourceEntry(entry) || isEmailSourceEntry(entry)) {
    return false;
  }

  return sourceKind === 'file'
    || sourceKind === 'image'
    || CAPTURE_DAY_RECEIPT_SOURCE_PATTERN.test(sourceType);
};

const isParsedSourceContainerEntry = (entry = {}) => {
  if (getTimelinePlannerCardKey(entry) || !getSourceNoteId(entry)) return false;

  return Boolean(
    entry.parsedObservation
    || entry.source?.parsedObservation
    || entry.metadata?.parsedObservation
    || /import|email|document|file|pdf|upload/.test(getSourceType(entry))
  );
};

const getSourceProcessingStatus = (entries = []) => normalizeText(entries
  .map((entry) => (
    entry.processingStatus
    || entry.status
    || entry.metadata?.processingStatus
    || entry.metadata?.careSourceProcessing?.status
  ))
  .find(Boolean)).toLowerCase();

const resolveSourceReceiptSummary = ({ entryCount = 0, status = '' } = {}) => {
  if (/organizing|processing|extracting|pending/.test(status)) {
    return 'This source is being organized.';
  }
  if (/failed|error|attention/.test(status)) {
    return 'This source needs attention.';
  }
  if (entryCount > 0) {
    return `${entryCount} care ${entryCount === 1 ? 'entry was' : 'entries were'} organized from this source.`;
  }
  return 'This source was added to Lifelog on this date.';
};

export const isSourceContainerReceiptEntry = isParsedSourceContainerEntry;

const getEntryCaptureTimestamp = (entry = {}) => [
  entry.source?.capturedAt,
  entry.metadata?.source?.capturedAt,
  entry.metadata?.capturedAt,
  entry.capturedAt,
  entry.createdAt,
  entry.metadata?.createdAt,
  entry.source?.createdAt,
  entry.loggedAt,
].map(toDate).find(Boolean) || null;

const getReceiptCaptureTimestamp = (entries = []) => entries
  .map(getEntryCaptureTimestamp)
  .filter(Boolean)
  .sort((left, right) => left - right)[0] || null;

const resolveSourceReceiptTitle = (entry = {}) => {
  const sourceType = normalizeText([
    entry.sourceMode,
    entry.inputMode,
    entry.source?.type,
    entry.source?.mode,
    entry.sourceKind,
    entry.metadata?.source?.sourceKind,
    entry.documentType,
    entry.metadata?.source?.documentType,
  ].filter(Boolean).join(' ')).toLowerCase();

  if (/email|gmail|message/.test(sourceType)) return 'Email added to Lifelog';
  if (/photo|image|camera/.test(sourceType)) return 'Photo added to Lifelog';
  if (/document|file|pdf|upload/.test(sourceType)) return 'Document added to Lifelog';
  return 'Note added to Lifelog';
};

export const getSourceCaptureReceiptDateKey = (entry = {}) => {
  if (
    !isCaptureDayReceiptEligibleEntry(entry)
    || !getSourceNoteId(entry)
    || !getTimelinePlannerCardKey(entry)
    || getSourceContainerKind(entry) === 'story'
  ) {
    return '';
  }

  const captureDateKey = formatDateKey(getEntryCaptureTimestamp(entry));
  const entryDateKey = formatDateKey(getTimelineEntryDate(entry));
  return captureDateKey && entryDateKey && captureDateKey !== entryDateKey ? captureDateKey : '';
};

export const buildSourceCaptureReceipts = (entries = []) => {
  const sourceEntriesById = new Map();
  const medicationReviewsBySourceId = new Map();
  entries.forEach((entry) => {
    const medicationReviewSourceId = getMedicationReviewSourceId(entry);
    if (medicationReviewSourceId) {
      medicationReviewsBySourceId.set(medicationReviewSourceId, entry);
      return;
    }
    const sourceNoteId = getSourceNoteId(entry);
    if (!sourceNoteId || getSourceContainerKind(entry) === 'story') return;
    if (!getTimelinePlannerCardKey(entry) && !isParsedSourceContainerEntry(entry)) return;
    if (!sourceEntriesById.has(sourceNoteId)) sourceEntriesById.set(sourceNoteId, []);
    sourceEntriesById.get(sourceNoteId).push(entry);
  });

  return [...sourceEntriesById.entries()].flatMap(([sourceNoteId, sourceEntries]) => {
    if (!sourceEntries.some(isCaptureDayReceiptEligibleEntry)) return [];

    const plannerEntries = sourceEntries.filter((entry) => getTimelinePlannerCardKey(entry));
    const sourceContainerEntries = sourceEntries.filter(isParsedSourceContainerEntry);
    const medicationReviewEntry = medicationReviewsBySourceId.get(sourceNoteId) || null;
    const medicationReviewSummary = medicationReviewEntry?.medicationReviewSummary || null;
    const isMedicationSource = Boolean(medicationReviewEntry) || sourceEntries.some((entry) => (
      getSourceContext(entry) === 'medication'
      || /medication/.test(getSourceType(entry))
    ));
    if (
      !sourceContainerEntries.length
      && !isMedicationSource
      && !plannerEntries.some((entry) => getSourceCaptureReceiptDateKey(entry))
    ) return [];
    const sortedPlannerEntries = [...plannerEntries].sort((left, right) => getTimelineEntryDate(left) - getTimelineEntryDate(right));
    const firstEntry = sourceContainerEntries[0] || sortedPlannerEntries[0] || {};
    const captureTimestamp = getReceiptCaptureTimestamp(sourceContainerEntries.length ? sourceContainerEntries : sortedPlannerEntries);
    const sourceContext = sourceEntries
      .map((entry) => getCaptureFirstTimelineContext(entry) || null)
      .find((context) => context?.hasVisibleContent)
      || getCaptureFirstTimelineContext(firstEntry)
      || null;
    const sourceReceiptTitle = medicationReviewSummary
      ? 'Medication document reviewed'
      : isMedicationSource
        ? 'Medication document uploaded'
        : resolveSourceReceiptTitle(firstEntry);
    const entryCount = sortedPlannerEntries.length;
    const sourceReceiptSummary = medicationReviewSummary
      ? getMedicationReviewReceiptSummary(medicationReviewSummary)
      : isMedicationSource
        ? getPendingMedicationReceiptSummary(sourceEntries)
        : resolveSourceReceiptSummary({
            entryCount,
            status: getSourceProcessingStatus(sourceContainerEntries),
          });

    return [{
      id: `source-receipt:${sourceNoteId}`,
      kind: 'source_receipt',
      collection: 'source_receipt',
      category: 'source_receipt',
      sourceReceipt: true,
      sourceReceiptOnly: true,
      sourceReceiptTitle,
      sourceReceiptSummary,
      medicationReviewSummary,
      storyDisplayLabel: sourceReceiptTitle,
      sourceStoryLabel: sourceReceiptTitle,
      sourceFileName: getSourceFileName(firstEntry) || normalizeText(sourceContext?.sourceAttachments?.[0]?.fileName),
      sourceStorySceneCount: entryCount,
      sourceStoryScenes: isMedicationSource ? [] : sortedPlannerEntries,
      sourceStoryContext: sourceContext,
      sourceNoteId,
      createdAt: captureTimestamp,
      loggedAt: captureTimestamp,
      timestamp: captureTimestamp,
      narrativeSortTime: captureTimestamp?.getTime?.() || 0,
      navigationTarget: `/sources/${sourceNoteId}`,
      body: sourceReceiptSummary,
    }];
  });
};
